//Personicom.NT.Reporting: src/features/reporting/reporting-helpers.ts
import { KeysetPaginatedList, PaginationInfo, ReportRow } from 'app-types';
import { ITimelineEvent } from "../report-timeline/report-timeline-types";
import { ReportGridColumn, ReportGridConfig } from "app-types";
import { jsonCombineFormatter, jsonFormatter, LinkCell, ReportStatus, TipCell } from "components/xgrid-helpers";
import { addDays, addWeeks, addMonths, addYears, startOfDay, endOfDay, format } from "date-fns";
import { doFetchAsJson, IPayloadError, logger, getApiBaseUrl } from "@personicom/customizations";
import { reportingLogger } from "helpers/reporting-logger";
import type { LogCategory } from '@personicom/customizations';
import { get, isString, pick } from "lodash";
import { FilterCondition, FilterValue, SortCondition } from './reporting-types';
import { GridColDef } from "@mui/x-data-grid";
import Handlebars from 'handlebars';

const DEFAULT_DATE_FORMAT = "M/d/yy h:mm:ss aaa";
const DEFAULT_DATE_ONLY_FORMAT = "M/d/yy";

export const hasMeaningfulValue = (value: unknown): boolean => {
  return !(value === undefined || value === null || (typeof value === "string" && value.trim().length === 0));
};

const toCamelVariant = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
};

const toPascalVariant = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const toSnakeVariant = (value: string): string => {
  return value ? value.replace(/([A-Z])/g, "_$1").toLowerCase() : value;
};

const buildVariantPaths = (field: string): string[] => {
  const trimmed = field?.trim();
  if (!trimmed) {
    return [];
  }

  const segments = trimmed.split('.');
  const key = segments.pop() ?? '';
  const prefix = segments.length > 0 ? `${segments.join('.')}.` : '';

  const variants = new Set<string>();
  const keyVariants = [
    key,
    toCamelVariant(key),
    toPascalVariant(key),
    toSnakeVariant(key),
    key.toLowerCase(),
    key.toUpperCase()
  ];

  keyVariants
    .filter(Boolean)
    .forEach(variant => variants.add(`${prefix}${variant}`));

  variants.add(trimmed);

  return Array.from(variants);
};

const resolveFieldFromSource = (source: any, field: string): unknown => {
  if (!source) {
    return undefined;
  }

  const candidates = buildVariantPaths(field);
  for (const candidate of candidates) {
    const value = get(source, candidate);
    if (hasMeaningfulValue(value)) {
      return value;
    }
  }

  return undefined;
};

export const resolveRowValue = (row: Record<string, any>, field: string): unknown => {
  if (!row || !field) {
    return undefined;
  }

  const directValue = resolveFieldFromSource(row, field);
  if (hasMeaningfulValue(directValue)) {
    return directValue;
  }

  if (row.renderParametersJson) {
    const renderParamsValue = resolveFieldFromSource(row.renderParametersJson, field);
    if (hasMeaningfulValue(renderParamsValue)) {
      return renderParamsValue;
    }
  }

  if (/^id$/i.test(field)) {
    const fallbackId = resolveFieldFromSource(row, 'renderId')
      ?? resolveFieldFromSource(row.renderParametersJson, 'UniqueId')
      ?? resolveFieldFromSource(row.renderParametersJson, 'SID');

    if (hasMeaningfulValue(fallbackId)) {
      return fallbackId;
    }
  }

  if (/renderid/i.test(field)) {
    const fallbackRenderId = resolveFieldFromSource(row, 'renderId')
      ?? resolveFieldFromSource(row.renderParametersJson, 'renderId')
      ?? resolveFieldFromSource(row.renderParametersJson, 'UniqueId');

    if (hasMeaningfulValue(fallbackRenderId)) {
      return fallbackRenderId;
    }
  }

  return undefined;
};

type NormalizedReportRow = ReportRow & Record<string, any>;

const assignIfMissing = (target: Record<string, any>, key: string, value: unknown) => {
  if (!target || !key) {
    return;
  }

  if (!hasMeaningfulValue(target[key]) && hasMeaningfulValue(value)) {
    target[key] = value;
  }
};

export const normalizeReportRow = (row: ReportRow, config?: ReportGridConfig): NormalizedReportRow => {
  const baseRow = row as unknown as Record<string, any>;
  const normalized: NormalizedReportRow = {
    ...row,
    renderParametersJson: { ...(row.renderParametersJson ?? {}) }
  };

  const renderIdCandidate = resolveRowValue(baseRow, 'renderId')
    ?? resolveFieldFromSource(row.renderParametersJson, 'renderId')
    ?? resolveFieldFromSource(row.renderParametersJson, 'UniqueId')
    ?? resolveFieldFromSource(row.renderParametersJson, 'SID');

  const idCandidate = resolveRowValue(baseRow, 'id')
    ?? renderIdCandidate
    ?? resolveFieldFromSource(row.renderParametersJson, 'UniqueId')
    ?? resolveFieldFromSource(row.renderParametersJson, 'SID');

  assignIfMissing(normalized, 'renderId', renderIdCandidate);
  assignIfMissing(normalized, 'id', idCandidate);

  const renderAliases = ['RenderId', 'RenderID', 'renderID'];
  const idAliases = ['Id', 'ID'];

  renderAliases.forEach(alias => assignIfMissing(normalized, alias, renderIdCandidate));
  idAliases.forEach(alias => assignIfMissing(normalized, alias, idCandidate ?? renderIdCandidate));

  const renderParams = normalized.renderParametersJson as Record<string, any>;

  renderAliases.forEach(alias => assignIfMissing(renderParams, alias, renderIdCandidate));
  idAliases.forEach(alias => assignIfMissing(renderParams, alias, idCandidate ?? renderIdCandidate));
  assignIfMissing(renderParams, 'UniqueId', resolveFieldFromSource(renderParams, 'UniqueId') ?? idCandidate ?? renderIdCandidate);
  assignIfMissing(renderParams, 'uniqueId', resolveFieldFromSource(renderParams, 'uniqueId') ?? idCandidate ?? renderIdCandidate);
  assignIfMissing(renderParams, 'SID', resolveFieldFromSource(renderParams, 'SID') ?? idCandidate ?? renderIdCandidate);

  if (config?.columns) {
    config.columns.forEach(col => {
      if (/renderid/i.test(col.field)) {
        assignIfMissing(normalized, col.field, renderIdCandidate);
        assignIfMissing(renderParams, col.field, renderIdCandidate);
      }

      if (/^id$/i.test(col.field)) {
        assignIfMissing(normalized, col.field, idCandidate ?? renderIdCandidate);
        assignIfMissing(renderParams, col.field, idCandidate ?? renderIdCandidate);
      }
    });
  }

  return normalized;
};

// Register a Handlebars helper for date formatting
Handlebars.registerHelper("formatDate", (date, dateFormat = DEFAULT_DATE_FORMAT) => {
  return formatDate(new Date(date), dateFormat);
});

// Utility function for date formatting
export const formatDate = (value: any, formatStr = DEFAULT_DATE_FORMAT) => {
  return value ? format(new Date(value), formatStr) : "";
};

// General-purpose relative date parser
export const parseRelativeDate = (value: string): Date | undefined => {
  const [_, sign, num, unit, offset] = value.match(/^([+-])(\d+)\.(days|weeks|months|years)\.(start|end)$/) || [];
  if (!sign || !num || !unit) return undefined;

  const amount = sign === "+" ? parseInt(num) : -parseInt(num);
  const baseDate = offset === "end" ? endOfDay(new Date()) : startOfDay(new Date());

  switch (unit) {
    case "days":
      return addDays(baseDate, amount);
    case "weeks":
      return addWeeks(baseDate, amount);
    case "months":
      return addMonths(baseDate, amount);
    case "years":
      return addYears(baseDate, amount);
    default:
      return baseDate;
  }
};

//-- Converts a relative date string in the reporting.json filter object to an actual date.
// date string will be in the form of +/-[num].[period].[offset]  So +0.days.start is start of today, -1.days.end is end of yesterday, +1.months.start is start of 1 month from today.
export const getDateFromProperty = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  
  const result = parseRelativeDate(value);

  return result;
};
type FilterFunc = (value: any) => string;

//-----
// Gets a value, whether it's based on a function or just string property
export function getFilterValue(filterProp: string | FilterFunc | undefined, defaultValue: string | undefined = undefined): string {
  let result = defaultValue ?? "";
  if (!filterProp) {
    result = "";
  }
  else if (isString(filterProp)) {
    result = filterProp as string;
  }
  // else if(isFunction(filterProp)){
  //   return (filterProp as FilterFunc)(data);
  // }  

  return result;
}

// Mapping properties that directly apply to GridColDef
const colProps = ["field", "headerName", "hide", "width", "sortable", "type", "sortingOrder"];

// Configure column definitions for DataGrid.
const buildColumn = (col: ReportGridColumn, environment: string, client: string, subdomain: string): GridColDef => {
  const gridColDef: GridColDef = pick(col, colProps) as GridColDef;

  // Step 1: Compile and set the valueGetter using `valueTemplate` if specified
  if (col.valueTemplate && (col.valueTemplate.includes("{{") || col.valueTemplate.includes("{{{"))) {
    try {
      const template = Handlebars.compile(col.valueTemplate);
      gridColDef.valueGetter = (params) => {
        // Provide entire row as context so nested properties like {{renderParametersJson.SID}} work
        return template(params.row);
      };
    } catch (error) {
      logger.error(`Error compiling template for field ${col.field}`, error, 'General');
      gridColDef.valueGetter = () => '';
    }
  }
  // If no valueTemplate, leave valueGetter undefined so formatter logic can handle it

  // Step 2: Apply formatter if no valueGetter was set
  if (!gridColDef.valueGetter && col.formatter) {
    switch (col.formatter) {
      case "json": //Obsolete use valueTemplate instead
        gridColDef.renderCell = col.formatterArgs ? jsonFormatter(col.formatterArgs) : undefined;
        const jsonProp = typeof col.formatterArgs === 'string' ? col.formatterArgs : 'renderParametersJson';
        gridColDef.valueGetter = (params) => {
          const nestedSource = params.row?.[jsonProp];
          const nestedValue = resolveFieldFromSource(nestedSource, col.field);
          if (hasMeaningfulValue(nestedValue)) {
            return nestedValue;
          }

          return resolveRowValue(params.row, col.field);
        };
        break;
      case "jsonCombine": //Obsolete use valueTemplate instead
        gridColDef.renderCell = col.formatterArgs ? jsonCombineFormatter(col.formatterArgs) : undefined;
        // For jsonCombine, we need to extract multiple fields - let renderCell handle it
        gridColDef.valueGetter = () => ''; // Placeholder to prevent default behavior
        break;
      case "date":
      case "dateTime":
        // These are handled by col.type in Step 3, not col.formatter
        break;   
      default:
        logger.warn(`Unsupported formatter for field ${col.field}`, undefined, 'General');
    }
  }

  // Step 3: Apply type-specific formatting with formatterArgs as custom format if it's a string
  if (col.type === "dateTime") {
    const dateFormat = typeof col.formatterArgs === "string" ? col.formatterArgs : DEFAULT_DATE_FORMAT;
    gridColDef.valueFormatter = (params) => formatDate(params.value, dateFormat);
  } else if (col.type === "date") {
    const dateFormat = typeof col.formatterArgs === "string" ? col.formatterArgs : DEFAULT_DATE_ONLY_FORMAT;
    gridColDef.valueFormatter = (params) => formatDate(params.value, dateFormat);
  }

  // Step 3.5: If no valueGetter was set by valueTemplate or formatter, use default field getter
  if (!gridColDef.valueGetter) {
    gridColDef.valueGetter = (params) => resolveRowValue(params.row, col.field);
  }
  
  // Step 4: Apply renderType-specific cell rendering (e.g., tooltips, links, status chips)
  if (col.renderType) {
    switch (col.renderType) {
      case "textWithTooltip":
        gridColDef.renderCell = TipCell(col.renderArgs);
        break;
      case "link":
        gridColDef.renderCell = LinkCell(col.renderArgs);
        break;
      case "status":
        gridColDef.renderCell = ReportStatus(col.renderArgs);
        break;
      default:
        logger.warn(`Unsupported renderType for field ${col.field}`, undefined, 'General');
    }
  }

  // Keep server-side sorting deterministic: only cycle between asc/desc.
  // MUI's default includes a third "unsorted" state, which can clear sorts on first click.
  if (gridColDef.sortable !== false && (!gridColDef.sortingOrder || gridColDef.sortingOrder.length === 0)) {
    gridColDef.sortingOrder = ['asc', 'desc'];
  }

  return gridColDef;
};


// Updated function to convert the grid configuration into DataGrid-compatible columns
export const getGridColumns = (gridConfig: ReportGridConfig, environment: string, client: string, subdomain: string): GridColDef[] => {
  let items: GridColDef[] = gridConfig.columns.map((col) => buildColumn(col, environment, client, subdomain));

  // Include hidden ID field if necessary
  if (gridConfig.idField !== "id" && !items.some(item => item.field === gridConfig.idField)) {
    items = [{ field: "id", headerName: "Id", hide: true, type: "string" }, ...items];
  }

  return items;
};



export function buildQueryString(
  pageNum: number | undefined,
  pageSize: number | undefined,
  sort?: string,
  filter?: string,
  other: Record<string, string> | null = null,
  bookmark?: string
): URLSearchParams {
  let qs = new URLSearchParams();

  // Use pageNum directly as 1-based index, default to 1 if undefined
  qs.append("pageNumber", (pageNum !== undefined ? pageNum : 1).toString());

  if (bookmark) {
    qs.append("KeysetBookmark", bookmark);
  } else {
    // Initial query, send sort and filter if no bookmark
    if (sort) qs.append("sort", sort);
    if (filter) qs.append("filter", filter);
  }

  // Only append pageSize if it is not undefined
  if (pageSize !== undefined) qs.append("pageSize", pageSize.toString());

  // Append other parameters if present
  if (other) {
    Object.keys(other).forEach(k => {
      if (other[k]) {
        qs.append(k, other[k]);
      }
    });
  }

  return qs;
}


//-----
// Replaces the reportDetails of a ReportRow, and updates the array (immutably)
export const replaceItemDetails = (items: ReportRow[], renderId: string, itemEvents: ITimelineEvent[]): ReportRow[] => {
  const toReplace = items.findIndex(i => i.renderId === renderId);
  if (toReplace === -1) {
    return items;
  }

  const replacement = {
    ...items[toReplace],
    reportDetails: itemEvents,
  };

  let newArray = items.slice(0);
  newArray[toReplace] = replacement;
  return newArray;
}

export const getReportData = async (
  client: string,
  subdomain: string,
  pageNum: number,
  pageSize: number,
  sorts?: SortCondition[],
  filters?: FilterCondition[],
  token?: string,
  bookmark?: string,
  clientCorrelationId?: string
): Promise<KeysetPaginatedList<ReportRow> | IPayloadError> => {

  logger.verbose('getReportData', { client, subdomain, pageNum, pageSize, sorts, filters }, 'API');

  let other: Record<string, string> = { rootDomain: client };
  if (subdomain) other.subdomain = subdomain;
  
  // Convert datetime-local format (yyyy-MM-ddTHH:mm) to full ISO strings for API
  const apiFilters = filters?.map(f => {
    // If value looks like a datetime-local format (16 chars, no timezone), convert to ISO
    if (typeof f.value === 'string' && f.value.length === 16 && f.value.includes('T')) {
      return { ...f, value: new Date(f.value).toISOString() };
    }
    return f;
  });

  // Drop "empty value" filters for operators that require a value.
  // URL deep-links can carry stale filters like `id contains ""`, which should not hit the API.
  const valueOptionalOperators = new Set(['isempty', 'isnotempty', 'isnull', 'isnotnull']);
  const normalizedFilters = (apiFilters ?? []).filter((filter) => {
    const operator = (filter.operatorValue ?? '').toLowerCase();
    if (valueOptionalOperators.has(operator)) {
      return true;
    }

    const value = filter.value;
    if (Array.isArray(value)) {
      return value.some((entry) => {
        if (entry === null || entry === undefined) return false;
        return typeof entry !== 'string' || entry.trim().length > 0;
      });
    }

    if (value === null || value === undefined) {
      return false;
    }

    return typeof value !== 'string' || value.trim().length > 0;
  });

  const includeTotalCount = shouldIncludeTotalCount(normalizedFilters);

  // Prepare the payload for the POST request
  const payload = {
    correlationId: clientCorrelationId,
    rootDomain: client,
    subdomain: subdomain || null,
    pageNumber: pageNum,
    pageSize: pageSize,
    filters: normalizedFilters, // Use normalized filters
    sorts: sorts?.length ? sorts : [], // Encode sorts directly
    encodedKeysetBookmark: bookmark || null,  // Must match C# property name: EncodedKeysetBookmark
    includeTotalCount
  };

  
  // API call endpoint
  const apiCall = `reports/videoanalytics`;

  // Make the POST request with the payload (no need to stringify here)
  return await doFetchAsJson(apiCall, "POST", payload, token);
};

/**
 * Disables expensive total-count queries for very wide date ranges to avoid SQL timeouts.
 */
export const shouldIncludeTotalCount = (filters?: FilterCondition[]): boolean => {
  if (!filters || filters.length === 0) {
    return true;
  }

  const startFilter = filters.find((filter) => {
    const op = (filter.operatorValue ?? '').toLowerCase();
    return op === 'onorafter' || op === 'after';
  });

  const endFilter = filters.find((filter) => {
    const op = (filter.operatorValue ?? '').toLowerCase();
    return op === 'onorbefore' || op === 'before';
  });

  const startDate = typeof startFilter?.value === 'string' ? new Date(startFilter.value) : null;
  const endDate = typeof endFilter?.value === 'string' ? new Date(endFilter.value) : null;

  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return true;
  }

  const rangeDays = Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  return rangeDays <= 180;
};

/**
 * Resolves an effective row count for server-side DataGrid pagination.
 * When totalCount is unknown (null from API), we synthesize a rowCount to keep next-page navigation usable.
 */
export const resolveServerRowCount = (
  pageNumber: number,
  pageSize: number,
  itemsOnPage: number,
  pagination?: PaginationInfo
): number => {
  const knownTotal = pagination?.totalCount;
  if (typeof knownTotal === 'number' && knownTotal > 0) {
    return knownTotal;
  }

  const safePageNumber = Math.max(pageNumber || 1, 1);
  const safePageSize = Math.max(pageSize || 1, 1);
  const safeItemsOnPage = Math.max(itemsOnPage || 0, 0);

  if (pagination?.hasNextPage) {
    return safePageNumber * safePageSize + 1;
  }

  if (safePageNumber === 1) {
    return safeItemsOnPage;
  }

  return (safePageNumber - 1) * safePageSize + safeItemsOnPage;
};

/**
 * True when the grid footer can show a real total: either the API returned one, or we
 * are on the last page (so rows-seen-so-far IS the total). When false, the rowCount fed
 * to the grid is the synthetic keep-paging-enabled value from resolveServerRowCount and
 * must not be presented as a finite total.
 */
export const isTotalCountKnown = (pagination?: PaginationInfo): boolean => {
  const knownTotal = pagination?.totalCount;
  if (typeof knownTotal === 'number' && knownTotal > 0) {
    return true;
  }

  return !pagination?.hasNextPage;
};

/**
 * Pagination label for ranges where the total count is suppressed (>180-day windows):
 * "1–100 of 100+" instead of a fake finite number.
 */
export const formatUnknownTotalLabel = ({ from, to }: { from: number; to: number }): string =>
  `${from}–${to} of ${to}+`;

export const getReportDetails = async (
  client: string, 
  subdomain: string, 
  renderId: string, 
  token?: string | null) => {

  let other: Record<string, string> = { rootDomain: client };
  if (subdomain) other.subdomain = subdomain;
  const qs = buildQueryString( undefined, undefined, undefined, undefined, other);
  const apiCall = `reports/videoanalytics/${renderId}?${qs}`

  return await doFetchAsJson(apiCall, "GET", null, token);
}


export const extractFileName = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) {
    return null;
  }

  // Match quoted values first so the closing quote is never captured as part of the
  // name (the previous greedy [^;]+ swallowed it, and the browser then turned the
  // illegal '"' into the infamous trailing '_'). Unquoted and RFC 5987 forms follow.
  const quoted = contentDisposition.match(/filename\*?=(?:UTF-8'')?"([^"]*)"/i);
  const unquoted = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;"\r\n]+)/i);
  const raw = quoted?.[1] ?? unquoted?.[1];

  if (raw && raw.trim().length > 0) {
    try {
      return decodeURIComponent(raw.trim());
    } catch {
      return raw.trim();
    }
  }

  return null;
};

export const sanitizeFileName = (fileName: string | null, fallback = 'report-export.csv'): string => {
  if (!fileName || fileName.trim().length === 0) {
    return fallback;
  }

  // Strip characters that are illegal in filenames on Windows/macOS before any other
  // normalization, so a stray quote or bracket can never reach the download attribute
  // (browsers replace such characters with '_').
  const legal = fileName.replace(/[<>:"/\\|?*]/g, '');
  if (legal.trim().length === 0) {
    return fallback;
  }

  const normalized = legal.trim().replace(/[\s]+/g, '_').replace(/_+$/g, '');
  const extensionIndex = normalized.lastIndexOf('.');

  if (extensionIndex === -1) {
    const noExtension = normalized.replace(/_+$/g, '');
    return noExtension.length > 0 ? noExtension : fallback;
  }

  const base = normalized.slice(0, extensionIndex).replace(/_+$/g, '');
  const extension = normalized.slice(extensionIndex + 1).replace(/^_+|_+$/g, '').toLowerCase();

  const sanitizedBase = base.length > 0 ? base : 'report-export';
  const sanitizedExtension = extension.length > 0 ? `.${extension}` : '.csv';

  return `${sanitizedBase}${sanitizedExtension}`;
};

const triggerFileDownload = async (response: Response, mimeType: string, fileName: string): Promise<boolean> => {
  try {
    const blob = await response.blob();
    const fileBlob = new Blob([blob], { type: mimeType });
    const url = window.URL.createObjectURL(fileBlob);
    const linkElement = document.createElement('a');
    linkElement.href = url;
    linkElement.download = fileName;
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    reportingLogger.error('Error during file download', error, 'Reporting');
    return false;
  }
};


export const getReportDownload = async (
  brand: string,
  subdomain: string,
  sorts?: SortCondition[],
  filters?: FilterCondition[],
  token?: string
) => {
  // Prepare the payload similar to the paginated request, but without pagination parameters
  const payload = {
    rootDomain: brand,
    subdomain: subdomain || null,
    filters: filters?.length ? filters : [],
    sorts: sorts?.length ? sorts : [],
  };

  // Make the POST request to the export endpoint
  const apiCall = `reports/videoanalytics/export`;

  try {
    const baseUrl = getApiBaseUrl().replace(/\/+$/, '') + '/';
    const formattedEndpoint = apiCall.startsWith('/') ? apiCall.slice(1) : apiCall;
    const url = `${baseUrl}${formattedEndpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['x-personicom-auth'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const contentType = response.headers.get('Content-Type') ?? '';
      let details: unknown = null;

      try {
        if (contentType.includes('application/json')) {
          details = await response.json();
        } else {
          details = await response.text();
        }
      } catch (parseError) {
        details = parseError;
      }

      return {
        isError: true,
        status: response.status,
        message: `Error fetching file: ${response.statusText || response.status}`,
        details,
        url
      };
    }

    const mimeType = response.headers.get('Content-Type') ?? 'application/octet-stream';
    const rawFileName = extractFileName(response.headers.get('Content-Disposition'));
    const sanitizedFileName = sanitizeFileName(rawFileName);

    return await triggerFileDownload(response, mimeType, sanitizedFileName);
  } catch (error: any) {
    reportingLogger.error('Error during file fetch', error, 'Reporting');
    return {
      isError: true,
      message: `Error during file fetch: ${error?.message ?? 'Unknown error'}`,
      details: error,
      url: apiCall,
    };
  }
};

type ValueGettor = (prop: string, data: any) => string;
const EXPR = /{(.*?)}/g;

//----
//Parses and extracts a value from a data structure based on a template
// e.g. {prop1} will return data.prop1, or {prop.subPropValue} will return data.prop.subPropValue, etc.
export const getTemplateValue = (templateProperty: string, data: any, defaultValue: string = "", valueFunc?: ValueGettor): string => {
  let output = templateProperty;
  let match = null;
  do {
    match = EXPR.exec(templateProperty);
    if (match) {
      const val = valueFunc ? valueFunc(match[1], data) : get(data, match[1]);
      output = output.replace(match[0], val ?? "");
    }
  } while (match);

  return output || defaultValue;
}

// Delimiters that are unlikely to appear in column names, operators, or values
const FILTER_DELIMITER = '~';
const SORT_DELIMITER = '~';
const ARRAY_DELIMITER = ',';  // New delimiter for separating values in arrays (e.g., date ranges)

// Helper to handle encoding individual values or arrays
const encodeValue = (value: FilterValue | undefined): string => {
  if (value === undefined || value === null) {
    return ''; // Encode undefined or null as an empty string
  }
  if (Array.isArray(value)) {
    return value.map(val => (val !== null ? encodeURIComponent(String(val)) : '')).join(ARRAY_DELIMITER);
  }
  return encodeURIComponent(String(value));
};


// Encodes the filters into a URL-safe string with delimiters
export const encodeFilterConditions = (filters: FilterCondition[]): string => {
  return filters
    .map(f => `${f.id ?? f.columnField}${FILTER_DELIMITER}${f.columnField}${FILTER_DELIMITER}${f.operatorValue}${FILTER_DELIMITER}${encodeValue(f.value)}`)
    .join('|');
};

// Helper to handle decoding of individual values or arrays
const decodeValue = (value: string | undefined): FilterValue => {
  if (!value) return null;
  const decodedValues = value.split(ARRAY_DELIMITER).map(val => decodeURIComponent(val));
  return decodedValues.length > 1 ? decodedValues : decodedValues[0] || null;
};

// Decodes a filter string back into FilterCondition[]
export const decodeFilterConditions = (filterString: string): FilterCondition[] => {
  if (!filterString) return [];
  
  try {
    const filters = filterString.split('|').map(f => {
      const parts = f.split(FILTER_DELIMITER);
      
      // V4 format requires exactly 4 parts: id~columnField~operatorValue~value
      // If we don't have 4 parts, this is likely an old V3 format or invalid URL
      if (parts.length !== 4) {
        console.warn(`[Reporting] Invalid filter format detected (expected 4 parts, got ${parts.length}): ${f}`);
        return null;
      }
      
      const [id, columnField, operatorValue, value] = parts;
      
      // Validate that required fields are present
      if (!columnField || !operatorValue) {
        console.warn(`[Reporting] Invalid filter: missing columnField or operatorValue: ${f}`);
        return null;
      }
      
      return {
        id,
        columnField,
        operatorValue,
        value: decodeValue(value)
      };
    });
    
    // Filter out any null entries (invalid filters)
    const validFilters = filters.filter(f => f !== null) as FilterCondition[];
    
    // If all filters were invalid, log a warning and return empty array
    if (validFilters.length === 0 && filterString.length > 0) {
      console.warn('[Reporting] All filters were invalid. Using default filters instead.');
    }
    
    return validFilters;
  } catch (error) {
    console.error('[Reporting] Error decoding filter string:', error);
    return [];
  }
};
// Encodes the sorts into a URL-safe string using delimiter
export const encodeSortConditions = (sorts: SortCondition[]): string => {
  return sorts.map(s => `${s.columnName}${SORT_DELIMITER}${s.direction}`).join("|");
};

// Decodes a sort string back into SortCondition[].
// Tolerant of pasted legacy V1 links (`?sort=views-desc`) and of garbage input: legacy
// tokens are converted, invalid tokens are dropped (a hyphenated pseudo-column reaches
// the API as a JSON path and 400s the whole report), and an empty result lets the
// caller fall back to the configured default sort so the user still gets a baseline report.
export const decodeSortConditions = (sortString: string): SortCondition[] => {
  if (!sortString) return [];

  const isValidColumnName = (name: string | undefined): boolean =>
    !!name && /^[A-Za-z_][A-Za-z0-9_.]*$/.test(name);

  const conditions = sortString.split('|').map((s): SortCondition | null => {
    const [columnName, direction] = s.split(SORT_DELIMITER);

    if (isValidColumnName(columnName)) {
      // Ensure direction is either 'asc' or 'desc', otherwise default to 'asc'
      const validDirection = direction === 'asc' || direction === 'desc' ? direction : 'asc';
      return { columnName, direction: validDirection };
    }

    // Legacy V1 reporting links encode sort as `field-direction` (e.g. views-desc).
    const legacy = (columnName ?? '').match(/^(.+)-(asc|desc)$/i);
    if (legacy && isValidColumnName(legacy[1])) {
      return { columnName: legacy[1], direction: legacy[2].toLowerCase() as 'asc' | 'desc' };
    }

    console.warn(`[Reporting] Ignoring invalid sort token in URL: ${s}`);
    return null;
  });

  return conditions.filter((c): c is SortCondition => c !== null);
};
